// Fila de sincronização. Cada saveAll() do legado vira um changeset com id próprio:
// reenviar após queda de rede é seguro (o servidor reconhece o id e não aplica duas vezes).
import { snapshot, diff, KEYS } from './changeset.js';

export const parseState = store => {
  const s = {};
  for (const [k, name] of Object.entries(KEYS)) { const raw = store.getItem(k); s[name] = raw ? JSON.parse(raw) : (name === 'settings' ? {} : []); }
  return s;
};
const appError = e => { try { return JSON.parse(e.details || e.detail || '{}').code || null; } catch { return null; } };

export function createSync({ store, api, clientId, uuid, persist, onStatus = () => {}, onRejected = () => {}, beforeDiff = () => {}, reload, retryMs = 5000, setTimer = setTimeout }) {
  let base = null, versions = {}, flushing = false, queue = persist.load(), timer = null, created = new Set();
  const status = s => onStatus({ state: s, pending: queue.length });
  const save = () => persist.save(queue);

  function capture() {
    if (!base) return;
    const state = parseState(store);
    beforeDiff(state);
    const cur = snapshot(state);
    const { ops, movements } = diff(base, cur, state, uuid);
    if (!ops.length && !movements.length) return;
    for (const op of ops) {
      const key = op.entity + ':' + op.id;
      const known = versions[op.entity]?.[op.id];
      if (op.isNew) { op.base_version = null; created.add(key); }
      else if (known !== undefined) op.base_version = known;
      else if (created.has(key)) op.fromPrev = true;   // criado em um envio ainda na fila
      else op.base_version = null;
      delete op.isNew;
    }
    queue.push({ id: uuid(), ops, movements });
    save();
    base = cur;
    return flush();
  }

  async function flush() {
    if (flushing) return;
    flushing = true; clearTimeout(timer);
    let mustReload = false;
    try {
      while (queue.length) {
        status('saving');
        const cs = queue[0];
        for (const op of cs.ops) if (op.fromPrev) { op.base_version = versions[op.entity]?.[op.id] ?? null; delete op.fromPrev; }
        let res;
        try {
          res = await api.applyChangeset(cs.id, clientId, cs.ops, cs.movements);
        } catch (e) {
          const code = appError(e);
          if (!code && !e.permanent) {               // rede/servidor: mantém na fila e tenta de novo
            status('offline'); timer = setTimer(flush, retryMs); return;
          }
          // Recusa de negócio: tudo o que veio depois foi calculado em cima disso, então a fila inteira volta.
          const rejected = queue; queue = []; save();
          onRejected({ code, message: e.message, changesets: rejected });
          mustReload = true; break;
        }
        for (const [entity, ids] of Object.entries(res.versions || {})) versions[entity] = { ...versions[entity], ...ids };
        // Saldo do servidor diferente do local = outro usuário movimentou no meio do caminho.
        if (base && (stockDiverged(res.stock || []) || cs.movements.some(m => !base.movIds.has(m.id)))) mustReload = true;
        queue.shift(); save();
      }
      status('synced');
    } finally { flushing = false; }
    if (mustReload) await refresh();
  }


  function stockDiverged(stock) {
    const last = {};                             // vários movimentos do mesmo item: vale o último
    for (const s of stock) last[s.kind === 'produto' ? `produto:${s.item_id}:${s.variation_id}` : `${s.kind}:${s.item_id}`] = Number(s.after);
    return Object.entries(last).some(([k, after]) => k in base.stock && Math.abs(base.stock[k] - after) > 1e-3);
  }

  async function refresh() {
    if (queue.length) return false;
    const loaded = await api.loadAll();
    store.loadSilently(loaded.entries);
    versions = loaded.versions; created = new Set();
    reload();
    rebase();
    return true;
  }
  function rebase() { base = snapshot(parseState(store)); }

  return { capture, flush, refresh, rebase,
    setVersions(v) { versions = v; },
    get pending() { return queue.length; }, get base() { return base; }, get versions() { return versions; } };
}
